import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import IconButton from "../../packages/IconButton";
import WPTertiaryButton from "../../packages/WPTertiaryButton";

import AdvancedSettings from "../../pages/analyseData/AdvancedSettings/AdvancedSettings";
import ChemicalLibraryDialog from "../chemicalLibrary/ChemicalLibraryDialog";
import FeatureTourGuide from "../FeatureTourGuide";
import {
  getFeatureTourSteps,
  isFeatureTourCompleted,
} from "../FeatureTourGuide/featureTour.utils";
import createNotification, { ERROR_NOTIFICATION } from "../Notifications";
import { getNavigationState } from "../PageNavigation/PageNavigation.utils";

import {
  FEATURE_SCOPE,
  FEATURE_TOUR_KEYS,
  FEATURE_TOUR_SESSION_KEYS,
} from "../../constants/global.constants";

import {
  ChemicalsIcon,
  ChevronLeftIcon,
  ChevronRight,
  CostsIcon,
  SettingsIcon,
} from "../../assets/icons";

import { getItemSession, saveItemSession } from "../../utils/session";

import { completeFeatureTour } from "../../network/cipAPI";

import "./index.scss";

const PlantMenuHeader = ({
  hideCosts,
  advancedSettingsConfig,
  navigationConfig = {},
  onCostsClick,
  setShowRevisitHint,
  setForceUserProfileOpen,
  tourRestartTrigger = 0,
  isLoading = false,
}) => {
  const location = useLocation();
  const { t } = useTranslation();

  const [chemicalLibraryOpen, setChemicalLibraryOpen] = useState(false);
  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false);
  const [featureTourRun, setFeatureTourRun] = useState(false);

  const hasAdvancedSettings =
    Array.isArray(advancedSettingsConfig?.enabledSections) &&
    advancedSettingsConfig.enabledSections.length > 0;

  const { activePageKey, onPreviousClick, onNextClick, isNextDisabled } =
    navigationConfig;
  const { navigationPages, activePageIndex } = getNavigationState({
    pathname: location.pathname,
    activePageKey,
  });
  const activePage = navigationPages[activePageIndex] || {};
  const hasNavigationControls = navigationPages.length > 0;
  const previousButtonLabel = t(
    activePage.previousLabelKey || "ValueStrings.back",
  );
  const nextButtonLabel = t(activePage.nextLabelKey || "ValueStrings.next");

  useEffect(() => {
    if (isLoading) {
      setFeatureTourRun(false);
      return;
    }

    if (!isFeatureTourCompleted(FEATURE_TOUR_KEYS.CHEMICAL_UTILITY_COST)) {
      setFeatureTourRun(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!tourRestartTrigger) {
      return;
    }

    if (isLoading) {
      return;
    }

    setFeatureTourRun(true);
  }, [tourRestartTrigger, isLoading]);

  const handleFeatureTourShowMe = (isOpen = true) => {
    setForceUserProfileOpen?.(Boolean(isOpen));
  };

  const handleFeatureTourComplete = async () => {
    setFeatureTourRun(false);
    setShowRevisitHint(true);

    if (!isFeatureTourCompleted(FEATURE_TOUR_KEYS.CHEMICAL_UTILITY_COST)) {
      try {
        const payload = {
          feature_key: FEATURE_TOUR_KEYS.CHEMICAL_UTILITY_COST,
        };
        const response = await completeFeatureTour(payload);
        if (response?.status === 200) {
          const cachedValue = getItemSession(
            FEATURE_TOUR_SESSION_KEYS.COMPLETED_LIST,
          );
          const completedList = cachedValue ? JSON.parse(cachedValue) : [];
          const updatedCompletedList = [
            ...new Set([...completedList, payload.feature_key]),
          ];
          saveItemSession(
            FEATURE_TOUR_SESSION_KEYS.COMPLETED_LIST,
            JSON.stringify(updatedCompletedList),
          );
        } else {
          throw new Error("Failed to complete feature tour");
        }
      } catch (error) {
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          "",
        );
      }
    }
  };

  const handleChemicalLibraryOpen = () => {
    setChemicalLibraryOpen(true);
  };

  const handleChemicalLibraryClose = () => {
    setChemicalLibraryOpen(false);
  };

  const handleAdvancedSettingsOpen = () => {
    if (hasAdvancedSettings) {
      setIsAdvancedSettingsOpen(true);
    }
  };

  const handleAdvancedSettingsClose = () => {
    setIsAdvancedSettingsOpen(false);
  };

  const MENU_ITEMS = [
    {
      id: "plant-chemical-library",
      key: "chemicals",
      labelKey: "plantMenu.chemicals",
      tooltipKey: "plantMenu.chemicalTooltip",
      Icon: ChemicalsIcon,
      onClick: handleChemicalLibraryOpen,
      hidden: hideCosts,
    },
    {
      id: "plant-utility-costs",
      key: "costs",
      labelKey: "plantMenu.costs",
      tooltipKey: "plantMenu.costsTooltip",
      Icon: CostsIcon,
      onClick: onCostsClick,
      hidden: hideCosts,
    },
    {
      id: "plant-advanced-settings",
      key: "advanced-settings",
      labelKey: "plantMenu.settings",
      tooltipKey: "plantMenu.settingsTooltip",
      Icon: SettingsIcon,
      onClick: handleAdvancedSettingsOpen,
      hidden: !hasAdvancedSettings,
    },
  ];

  const visibleMenuItems = MENU_ITEMS.filter(({ hidden }) => !hidden);

  return (
    <div className="plant-menu-header">
      {hasNavigationControls && (
        <WPTertiaryButton
          id="plant-ribbon-previous"
          label={previousButtonLabel}
          icon={<ChevronLeftIcon />}
          iconPlacement="before"
          variant="text"
          className="plant-menu-header__nav-btn-prev"
          onClick={onPreviousClick}
          preventDefault={false}
        />
      )}
      <FeatureTourGuide
        key={tourRestartTrigger}
        run={featureTourRun}
        steps={getFeatureTourSteps(
          t,
          FEATURE_TOUR_KEYS.CHEMICAL_UTILITY_COST,
          location.pathname,
        )}
        showMeIndices={{ start: 2, end: 4 }}
        showMeCallback={handleFeatureTourShowMe}
        onComplete={handleFeatureTourComplete}
      />
      {visibleMenuItems.length > 0 && (
        <div className="plant-menu-items">
          {visibleMenuItems.map(
            ({ id, key, labelKey, tooltipKey, Icon, onClick, disabled }) => (
              <IconButton
                key={key}
                id={id}
                className="plant-menu-item"
                onClick={onClick}
                disabled={disabled}
                tooltip={t(tooltipKey)}
                placement="bottom"
              >
                <div>
                  <Icon width={20} height={20} />
                </div>
                <span className="plant-menu-item__label">{t(labelKey)}</span>
              </IconButton>
            ),
          )}

          {chemicalLibraryOpen && (
            <ChemicalLibraryDialog
              scope={FEATURE_SCOPE.PLANT}
              onClose={handleChemicalLibraryClose}
            />
          )}
          {isAdvancedSettingsOpen && (
            <AdvancedSettings
              handleClose={handleAdvancedSettingsClose}
              selectedTrain={advancedSettingsConfig?.selectedTrain}
              enabledSections={advancedSettingsConfig?.enabledSections}
              onSave={advancedSettingsConfig?.onSave}
              excludeFields={advancedSettingsConfig?.excludeFields || []}
            />
          )}
        </div>
      )}

      {hasNavigationControls && (
        <WPTertiaryButton
          id="plant-ribbon-next"
          label={nextButtonLabel}
          icon={<ChevronRight />}
          iconPlacement="after"
          variant="contained"
          type="secondary"
          className="plant-menu-header__nav-btn-next"
          disabled={Boolean(isNextDisabled)}
          onClick={onNextClick}
          preventDefault={false}
        />
      )}
    </div>
  );
};

export default PlantMenuHeader;
